import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Slider } from "../../components/ui/slider";
import { ArrowLeft, FileText, Tag, Receipt, Trash2, MoreVertical, Send, ScanLine, Upload, AlertTriangle, X, Loader2, Volume2, Play, Settings, StickyNote, Truck, Printer } from "lucide-react";
import { toast } from "sonner";
import { printLabelFromPdfUrl } from "../../lib/depotLabelPrint";
import { VALID_SOUNDS, PARTIAL_SOUNDS, INVALID_SOUNDS, getScanSoundSettings, setScanSoundSettings, playScanSound, previewSound } from "../../lib/scanSounds";

// Types de problème qu'on peut signaler sur une ligne du bon de commande, avant
// l'envoi à la boutique. Doit rester synchronisé avec ISSUE_LABELS côté backend
// (routers/depot_routes.py).
const ISSUE_TYPES = [
  { value: "manquant", label: "Produit manquant" },
  { value: "qte_manquante", label: "Quantité manquante" },
  { value: "doublon", label: "Doublon" },
  { value: "introuvable", label: "Introuvable" },
];
const ISSUE_LABEL_MAP = Object.fromEntries(ISSUE_TYPES.map((t) => [t.value, t.label]));

// Joue un bip de retour scan (voir lib/scanSounds.js pour le choix du son et
// du volume, réglables par l'opérateur depuis le bouton "Scanner prêt").
const beep = playScanSound;

function vibrate(ok) {
  if (navigator.vibrate) navigator.vibrate(ok ? 60 : [80, 60, 80]);
}

// ---------------------------------------------------------------------------
// File d'attente locale (tap / scan) — voir `applyQueueToOrder` plus bas.
//
// Objectif : que le clic ou le scan se voie changer À L'ÉCRAN immédiatement,
// avant même que la requête au serveur ne soit partie ou ait abouti — pour ne
// plus dépendre du temps de trajet réseau (notamment loin de la box). Chaque
// action est :
//   1. ajoutée à une file en mémoire + localStorage (donc jamais perdue,
//      même si l'app est fermée/rechargée avant confirmation du serveur),
//   2. immédiatement simulée par-dessus le dernier état confirmé pour
//      l'affichage (`applyQueueToOrder`),
//   3. envoyée au serveur en tâche de fond ; en cas d'échec réseau (pas de
//      réponse du serveur, ex. coupure Wi-Fi), elle reste en file et est
//      retentée automatiquement (au retour de connexion + par intervalle) ;
//      en cas de refus définitif du serveur (ligne déjà complète, code
//      inconnu...), elle est simplement retirée de la file et l'utilisateur
//      est prévenu — l'affichage revient alors tout seul à l'état réel
//      puisqu'il est recalculé à partir de la file restante.
//
// Ces fonctions pures reproduisent exactement la logique de
// routers/depot_routes.py (_max_pickable / _numeric_part / _lines_status) :
// à maintenir synchronisées si cette logique change côté serveur.

function maxPickable(line) {
  const attendue = line.quantite_attendue || 0;
  const issueType = line.issue_type;
  if (!issueType) return attendue;
  if (issueType === "qte_manquante") {
    const manquante = line.issue_quantite_manquante || 0;
    return Math.max(0, attendue - manquante);
  }
  return 0;
}

function numericPart(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? parseInt(digits, 10) : null;
}

function linesStatus(lines) {
  if (!lines || lines.length === 0) return "en_attente";
  const allDone = lines.every((l) => (l.quantite_picked || 0) >= maxPickable(l));
  if (allDone) return "termine";
  return lines.some((l) => (l.quantite_picked || 0) > 0) ? "en_cours" : "en_attente";
}

// Rejoue une action en attente sur une copie des lignes ; renvoie
// `{ ok, lines, reason?, line? }` (ok=false si l'action serait de toute façon
// refusée par le serveur — permet de valider une action, avec le même
// message d'erreur que le serveur, AVANT de l'ajouter à la file, sans
// attendre un aller-retour réseau pour le savoir).
function applyOneAction(lines, action) {
  if (action.type === "tap") {
    const line = lines.find((l) => l.id === action.lineId);
    if (!line) return { ok: false, lines, reason: "not_found" };
    const max = maxPickable(line);
    if (max <= 0) return { ok: false, lines, reason: "blocked", line };
    return { ok: true, lines: lines.map((l) => (l.id === line.id ? { ...l, quantite_picked: Math.min(max, (l.quantite_picked || 0) + 1) } : l)) };
  }
  if (action.type === "scan") {
    const scanned = numericPart(action.code);
    if (scanned == null) return { ok: false, lines, reason: "unreadable" };
    const candidates = lines.filter((l) => numericPart(l.ugs) === scanned);
    if (candidates.length === 0) return { ok: false, lines, reason: "no_match" };
    const line = candidates.find((l) => (l.quantite_picked || 0) < maxPickable(l)) || candidates[0];
    const max = maxPickable(line);
    if (max <= 0) return { ok: false, lines, reason: "blocked", line };
    if ((line.quantite_picked || 0) >= max) return { ok: false, lines, reason: "complete", line };
    return { ok: true, lines: lines.map((l) => (l.id === line.id ? { ...l, quantite_picked: (l.quantite_picked || 0) + 1 } : l)), line };
  }
  return { ok: false, lines, reason: "unknown_action" };
}

function applyQueueToOrder(order, queue) {
  if (!order) return order;
  let lines = order.lines || [];
  for (const action of queue) {
    const result = applyOneAction(lines, action);
    // Une action déjà en file est affichée même si elle deviendra caduque une
    // fois envoyée (ex. deux scans identiques en attente alors qu'un seul est
    // encore possible) : on l'ignore silencieusement à l'affichage plutôt que
    // de faire planter le rendu, le serveur tranchera à l'envoi réel.
    lines = result.lines;
  }
  return { ...order, lines, status: linesStatus(lines) };
}

function newActionId() {
  return (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function totals(order) {
  const total = (order.lines || []).reduce((s, l) => s + l.quantite_attendue, 0);
  const picked = (order.lines || []).reduce((s, l) => s + l.quantite_picked, 0);
  const percent = total > 0 ? Math.round((picked / total) * 100) : 0;
  const lineCount = (order.lines || []).length;
  return { total, picked, percent, lineCount };
}

function LinePill({ line }) {
  const done = line.quantite_picked >= line.quantite_attendue;
  return (
    <span
      data-testid={`depot-line-pill-${line.id}`}
      className={`min-h-[56px] min-w-[64px] px-4 rounded-full font-heading text-lg font-bold border-2 flex items-center justify-center transition-colors duration-150 ${
        done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-card border-foreground/20 text-foreground"
      }`}
    >
      {line.quantite_picked}/{line.quantite_attendue}
    </span>
  );
}

// Suivi Chronopost (voir chronopost_tracking.py, service public
// trackSkybillV2 — sans compte ni clé API) : trois statuts simplifiés,
// jusqu'à la livraison qui clôt le suivi.
const TRACKING_STATUS_LABEL = {
  en_preparation: "En préparation chez l'expéditeur",
  depose_relais: "Déposé au relais",
  en_cours_acheminement: "En cours d'acheminement",
  livre: "Livré",
  inconnu: "Statut inconnu",
};
const TRACKING_STATUS_STYLE = {
  en_preparation: "text-slate-600 bg-slate-500/10 border-slate-500/30",
  depose_relais: "text-sky-700 bg-sky-500/10 border-sky-500/30",
  en_cours_acheminement: "text-amber-700 bg-amber-500/10 border-amber-500/30",
  livre: "text-emerald-700 bg-emerald-500/10 border-emerald-500/30",
  inconnu: "text-muted-foreground border-border",
};

export default function DepotOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [shops, setShops] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingNumberInput, setTrackingNumberInput] = useState("");
  const [savingTrackingNumber, setSavingTrackingNumber] = useState(false);
  const [checkingTracking, setCheckingTracking] = useState(false);
  const [targetShop, setTargetShop] = useState("");
  const [flashLineId, setFlashLineId] = useState(null);
  const [issueDialogLine, setIssueDialogLine] = useState(null);
  const [issueType, setIssueType] = useState("");
  const [issueQty, setIssueQty] = useState("");
  // Verrou anti double-clic : évite qu'un clic répété (pendant que le serveur met
  // du temps à répondre) déclenche l'envoi en double de la commande à la boutique.
  const [sending, setSending] = useState(false);
  const scanBufferRef = useRef("");
  const lastKeyTimeRef = useRef(0);
  const invoiceInputRef = useRef(null);
  // Réglages des sons de scan (bruit "valide"/"invalide" + volume), ouverts en
  // cliquant sur le badge "Scanner prêt".
  const [soundSettingsOpen, setSoundSettingsOpen] = useState(false);
  const [soundSettings, setSoundSettings] = useState(() => getScanSoundSettings());

  // ---- File d'attente locale (tap / scan) — voir applyQueueToOrder plus haut ----
  const [queue, setQueue] = useState([]);
  // Miroir synchrone de `queue`, lu par flushQueue : `setState` ne met à jour
  // la valeur qu'au prochain rendu, ce qui serait trop tard pour envoyer une
  // action tout juste ajoutée dès cette même fonction (voir `enqueue`).
  const queueRef = useRef([]);
  const queueStorageKey = `depot-queue-${id}`;
  const flushingRef = useRef(false);
  const retryTimerRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Vue affichée = dernier état confirmé par le serveur + actions en attente
  // simulées par-dessus. Toujours recalculée, jamais stockée telle quelle.
  const displayOrder = useMemo(() => (order ? applyQueueToOrder(order, queue) : null), [order, queue]);

  const persistQueue = (next) => {
    queueRef.current = next;
    setQueue(next);
    try { localStorage.setItem(queueStorageKey, JSON.stringify(next)); } catch { /* stockage plein/indisponible : tant pis, la file reste en mémoire pour cette session */ }
  };

  const scheduleRetry = () => {
    if (retryTimerRef.current) return;
    const delay = retryDelayRef.current;
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      // Backoff progressif (1s → 2s → 4s ... plafonné à 15s) tant que la
      // connexion ne revient pas, pour ne pas marteler le serveur inutilement.
      retryDelayRef.current = Math.min(delay * 2, 15000);
      flushQueue();
    }, delay);
  };

  const flushQueue = async () => {
    if (flushingRef.current) return;
    flushingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const action = queueRef.current[0];
        try {
          let data;
          if (action.type === "tap") {
            ({ data } = await api.post(`/depot/orders/${id}/lines/${action.lineId}/tap`, {}, { timeout: 8000 }));
          } else {
            ({ data } = await api.post(`/depot/orders/${id}/scan`, { code: action.code }, { timeout: 8000 }));
            setFlashLineId(data.scanned_line_id);
            setTimeout(() => setFlashLineId(null), 900);
          }
          setOrder(data);
          persistQueue(queueRef.current.slice(1));
          retryDelayRef.current = 1000;
          setIsOffline(false);
        } catch (err) {
          if (err.response) {
            // Refus définitif du serveur (ligne déjà complète, code inconnu,
            // article signalé...) : cette action précise est caduque, on la
            // retire et on prévient — l'affichage revient tout seul à l'état
            // réel via applyQueueToOrder. On continue avec le reste de la file.
            toast.error(formatApiError(err.response?.data?.detail) || "Action refusée par le serveur");
            if (action.type === "scan") beep(false);
            persistQueue(queueRef.current.slice(1));
            continue;
          }
          // Pas de réponse du tout : coupure réseau. On laisse l'action en
          // tête de file (rien n'est perdu) et on retentera automatiquement.
          setIsOffline(true);
          scheduleRetry();
          return;
        }
      }
    } finally {
      flushingRef.current = false;
    }
  };

  const enqueue = (action) => {
    persistQueue([...queueRef.current, action]);
    flushQueue();
  };

  // Récupère une file laissée en attente (app fermée/rechargée avant la fin
  // de la synchronisation) dès qu'on ouvre cette commande, et relance l'envoi.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(queueStorageKey) || "[]");
      if (Array.isArray(saved) && saved.length > 0) {
        persistQueue(saved);
        flushQueue();
      }
    } catch { /* file locale corrompue/absente : on repart d'une file vide */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Retente dès que le navigateur signale un retour de connexion, en plus du
  // backoff périodique déjà en place pendant une coupure.
  useEffect(() => {
    const onOnline = () => { retryDelayRef.current = 1000; flushQueue(); };
    const onOffline = () => setIsOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = async () => {
    const { data } = await api.get(`/depot/orders/${id}`);
    setOrder(data);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/shops").then((r) => setShops(r.data.filter((s) => s.type === "boutique"))); }, []);

  const tap = (lineId) => {
    // Validation instantanée en local (mêmes règles que le serveur, voir
    // applyOneAction) avant d'ajouter l'action à la file : si elle serait de
    // toute façon refusée (article signalé, quantité disponible nulle), on le
    // sait tout de suite, sans round-trip réseau. Sinon, l'action part en
    // file d'attente et l'écran change immédiatement (voir enqueue /
    // applyQueueToOrder) — l'envoi réel au serveur se fait en tâche de fond.
    const base = displayOrder || order;
    const result = applyOneAction(base.lines || [], { type: "tap", lineId });
    if (!result.ok) {
      if (result.reason === "blocked") {
        toast.error(`« ${result.line.description || ""} » est signalé « ${ISSUE_LABEL_MAP[result.line.issue_type] || ""} » : aucune quantité disponible à valider`);
      }
      return;
    }
    enqueue({ id: newActionId(), type: "tap", lineId });
  };

  const resetLine = async (lineId) => {
    try {
      const { data } = await api.post(`/depot/orders/${id}/lines/${lineId}/reset`);
      setOrder(data);
      toast.success("Quantité remise à zéro");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Un simple clic valide une unité (tap). Pour ANNULER (revenir à zéro), il
  // faut MAINTENIR appuyé — jamais un simple clic — afin qu'un clic
  // involontaire une fois la quantité maximum atteinte n'efface jamais la
  // validation déjà faite sur la ligne.
  const HOLD_MS = 600;
  const [holdingLineId, setHoldingLineId] = useState(null);
  const holdTimerRef = useRef(null);
  const holdTriggeredRef = useRef(false);

  const startHold = (lineId) => {
    holdTriggeredRef.current = false;
    setHoldingLineId(lineId);
    holdTimerRef.current = setTimeout(() => {
      holdTriggeredRef.current = true;
      setHoldingLineId(null);
      resetLine(lineId);
    }, HOLD_MS);
  };

  const cancelHold = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setHoldingLineId(null);
  };

  const handleLineClick = (lineId) => {
    // Si l'appui maintenu vient de déclencher la remise à zéro, le clic qui
    // suit (relâchement du bouton) ne doit surtout pas en plus compter comme
    // une validation normale.
    if (holdTriggeredRef.current) {
      holdTriggeredRef.current = false;
      return;
    }
    tap(lineId);
  };

  const openIssueDialog = (line) => {
    setIssueDialogLine(line);
    setIssueType(line.issue_type || "");
    setIssueQty(line.issue_quantite_manquante ? String(line.issue_quantite_manquante) : "");
  };

  const submitIssue = async () => {
    if (!issueType) {
      toast.error("Choisissez un type de problème");
      return;
    }
    if (issueType === "qte_manquante" && (!issueQty || Number(issueQty) <= 0)) {
      toast.error("Indiquez la quantité manquante");
      return;
    }
    try {
      const { data } = await api.post(`/depot/orders/${id}/lines/${issueDialogLine.id}/issue`, {
        type: issueType,
        quantite_manquante: issueType === "qte_manquante" ? Number(issueQty) : undefined,
      });
      setOrder(data);
      toast.success("Problème signalé sur l'article");
      setIssueDialogLine(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const clearIssue = async () => {
    try {
      const { data } = await api.post(`/depot/orders/${id}/lines/${issueDialogLine.id}/issue`, { type: null });
      setOrder(data);
      toast.success("Signalement retiré");
      setIssueDialogLine(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Zebra RS5100 (Bluetooth HID / "keyboard wedge") scans by typing the code
  // very fast followed by Enter, wherever the focus is on the page. We buffer
  // fast keystrokes and treat an Enter after a burst of typing as a scan.
  const handleScan = (code) => {
    if (!code || code.length < 3) return;
    // Même principe que tap() ci-dessus : on rejoue localement le matching
    // scanné/UGS (mêmes règles que le serveur, voir applyOneAction) pour
    // savoir INSTANTANÉMENT si le scan est valide — bip et incrément
    // immédiats, sans attendre la réponse du serveur. L'action part ensuite
    // en file d'attente comme pour un tap.
    const base = displayOrder || order;
    const result = applyOneAction(base.lines || [], { type: "scan", code });
    if (!result.ok) {
      beep(false);
      vibrate(false);
      if (result.reason === "no_match") toast.error(`Aucun produit ne correspond au code ${code}`);
      else if (result.reason === "blocked") toast.error(`« ${result.line.description || ""} » est signalé « ${ISSUE_LABEL_MAP[result.line.issue_type] || ""} » : aucune quantité disponible à scanner`);
      else if (result.reason === "complete") toast.error(`« ${result.line.description || ""} » est déjà complet (${result.line.quantite_picked}/${maxPickable(result.line)})`);
      else toast.error(`Code illisible : ${code}`);
      return;
    }
    setFlashLineId(result.line.id);
    setTimeout(() => setFlashLineId(null), 900);
    const max = maxPickable(result.line);
    const nouveauCompte = Math.min((result.line.quantite_picked || 0) + 1, max);
    // Bug corrigé (demande explicite) : un seul bip "valide" existait, qu'il
    // reste 1 ou plusieurs exemplaires à scanner sur la ligne — impossible de
    // savoir sans regarder l'écran si on avait bien fini. Un bip distinct
    // "partiel" est désormais joué tant que la quantité demandée n'est pas
    // encore atteinte ; le bip "complet" habituel n'intervient que sur le
    // DERNIER exemplaire scanné de la ligne.
    const statutBip = nouveauCompte >= max ? "valid" : "partial";
    beep(statutBip);
    vibrate(true);
    toast.success(`${result.line.description} — ${nouveauCompte}/${max}`);
    enqueue({ id: newActionId(), type: "scan", code });
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;

      const now = Date.now();
      if (now - lastKeyTimeRef.current > 80) scanBufferRef.current = "";
      lastKeyTimeRef.current = now;

      if (e.key === "Enter" || e.key === "Tab") {
        if (scanBufferRef.current.length >= 3) {
          e.preventDefault();
          // Bug corrigé : si l'opérateur vient de valider une ligne au toucher
          // (clic/tap), cette ligne garde le focus clavier. Sans stopPropagation,
          // le "Entrée" envoyé par le scanner à la fin du code-barres continue
          // sa route jusqu'à cette ligne toujours focus et déclenche AUSSI son
          // propre onKeyDown (qui appelle tap() comme un appui sur Entrée/Espace
          // normal) : la dernière ligne validée au toucher se retrouvait donc
          // incrémentée une seconde fois par erreur à chaque scan suivant.
          e.stopPropagation();
          const code = scanBufferRef.current;
          scanBufferRef.current = "";
          // Sécurité supplémentaire : on retire le focus de l'élément actif
          // (la ligne tapée au toucher) pour qu'un futur Entrée/Espace égaré
          // ne puisse plus la re-déclencher.
          if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
            document.activeElement.blur();
          }
          // On passe par la ref plutôt que d'appeler handleScan directement :
          // cet effet ne se relance que si `id` change, donc la fonction
          // fermée ici (closure) resterait figée sur l'état (order/queue) du
          // tout premier montage. handleScanRef.current est maintenue à jour
          // à chaque rendu (voir juste après ce useEffect) et pointe donc
          // toujours vers la version la plus récente, avec l'état le plus
          // frais — indispensable puisque handleScan lit désormais l'état
          // local pour valider/afficher le scan instantanément.
          handleScanRef.current(code);
        }
        return;
      }
      if (e.key.length === 1) {
        scanBufferRef.current += e.key;
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleScanRef = useRef(handleScan);
  useEffect(() => { handleScanRef.current = handleScan; });

  // Imprime l'étiquette colis liée à la commande : recadrée et ajustée
  // automatiquement au format 101 x 150 mm (impression directe dans
  // l'application SogoGestion Desktop, fenêtre d'impression sinon).
  const printOrderLabel = async () => {
    const t = toast.loading("Préparation de l'étiquette...");
    try {
      const res = await printLabelFromPdfUrl(`${process.env.REACT_APP_BACKEND_URL}${order.label_pdf_url}`);
      if (res.ok) toast.success(window.SogoDesktop?.printImage ? "Étiquette envoyée à l'imprimante" : "Étiquette prête à imprimer", { id: t });
      else toast.error(res.error || "Échec de l'impression", { id: t });
    } catch (e) {
      toast.error(e.message || "Impossible de préparer l'étiquette", { id: t });
    }
  };

  const removeOrder = async () => {
    try {
      await api.delete(`/depot/orders/${id}`);
      toast.success("Commande supprimée");
      navigate("/depot");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveTrackingNumber = async () => {
    if (!trackingNumberInput.trim()) { toast.error("Indiquez un numéro de suivi"); return; }
    setSavingTrackingNumber(true);
    try {
      const { data } = await api.patch(`/depot/orders/${id}/tracking-number`, { tracking_number: trackingNumberInput.trim() });
      setOrder(data);
      toast.success("Numéro de suivi enregistré");
      setTrackingDialogOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingTrackingNumber(false);
    }
  };

  const checkTrackingNow = async () => {
    setCheckingTracking(true);
    try {
      const { data } = await api.post(`/depot/orders/${id}/check-tracking`);
      setOrder(data);
      toast.success(`Suivi mis à jour : ${TRACKING_STATUS_LABEL[data.tracking_status] || "statut inconnu"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCheckingTracking(false);
    }
  };

  const updateSoundSettings = (patch) => {
    setSoundSettings((prev) => {
      const next = { ...prev, ...patch };
      setScanSoundSettings(next);
      return next;
    });
  };

  const sendToShop = async () => {
    if (sending) return;
    if (!targetShop) {
      toast.error("Sélectionnez une boutique destinataire");
      return;
    }
    setSending(true);
    try {
      await api.post(`/depot/orders/${id}/send`, { shop_id: targetShop });
      toast.success("Bon de livraison envoyé à la boutique");
      setSendOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const pickInvoice = () => invoiceInputRef.current?.click();

  const attachInvoice = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("invoice", file);
    try {
      const { data } = await api.post(`/depot/orders/${id}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Facture liée · ${data.invoice_lines_matched} prix d'achat récupérés`);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    }
  };

  if (!order) return null;
  const { total, picked, percent, lineCount } = totals(displayOrder);

  return (
    <Layout title="Dépôt">
      <style>{`@keyframes depot-hold-progress { from { transform: scaleX(0); } to { transform: scaleX(1); } }`}</style>
      {/* Sticky : reste visible en haut pendant qu'on scrolle la liste des articles,
          pour toujours garder le numéro de commande / destinataire / avancement
          sous les yeux pendant le picking. Décalé de top-16 (mobile) / top-[72px]
          pour se coller juste sous la topbar (elle-même sticky top-0). */}
      <div className="sticky top-16 sm:top-[72px] z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-4 mb-2 bg-background/95 backdrop-blur-sm border-b border-border no-print">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <Link to="/depot" data-testid="depot-back-button" className="p-2 rounded-lg border border-border hover:bg-accent transition-colors duration-200 shrink-0">
              <ArrowLeft size={18} />
            </Link>
            <div className="min-w-0">
              <p className="font-heading text-xl sm:text-2xl font-bold truncate" data-testid="depot-order-title">CMD {order.numero}</p>
              {order.destinataire && (
                <p className="text-xs text-muted-foreground truncate" data-testid="depot-order-destinataire">{order.destinataire}</p>
              )}
              <p className="text-sm text-muted-foreground" data-testid="depot-order-progress">
                {picked}/{total} · {percent}% <span className="text-muted-foreground/70">· {lineCount} ligne{lineCount > 1 ? "s" : ""}</span>
              </p>
              {order.tracking_number && (
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${TRACKING_STATUS_STYLE[order.tracking_status] || "text-muted-foreground border-border"}`} data-testid="depot-tracking-status-badge">
                    {TRACKING_STATUS_LABEL[order.tracking_status] || "Suivi non encore vérifié"}
                  </span>
                  <button
                    type="button"
                    onClick={checkTrackingNow}
                    disabled={checkingTracking}
                    title="Vérifier maintenant le suivi Chronopost"
                    className="text-xs text-primary hover:underline disabled:opacity-50"
                    data-testid="depot-check-tracking-now"
                  >
                    {checkingTracking ? "Vérification..." : "Vérifier maintenant"}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {queue.length > 0 && (
              <span
                title={isOffline ? "Connexion coupée : reprendra automatiquement" : "Synchronisation avec le serveur en cours"}
                className={`flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border ${
                  isOffline ? "text-amber-700 bg-amber-500/10 border-amber-500/30" : "text-sky-700 bg-sky-500/10 border-sky-500/30"
                }`}
                data-testid="depot-sync-pending"
              >
                <Loader2 size={13} className={isOffline ? "" : "animate-spin"} />
                {isOffline ? `Hors ligne · ${queue.length} en attente` : `Synchro… ${queue.length}`}
              </span>
            )}
            <button
              type="button"
              onClick={() => setSoundSettingsOpen(true)}
              title="Régler les sons du scanner"
              className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1.5 hover:bg-emerald-500/20 transition-colors duration-150"
              data-testid="depot-scanner-ready"
            >
              <ScanLine size={14} /> <span className="hidden sm:inline">Scanner prêt</span> <Settings size={12} className="opacity-70" />
            </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="depot-actions-button" className="gap-2 shrink-0">ACTIONS <MoreVertical size={16} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <a href={`${process.env.REACT_APP_BACKEND_URL}${order.delivery_pdf_url}`} target="_blank" rel="noreferrer" data-testid="depot-view-delivery" className="flex items-center gap-2 cursor-pointer">
                  <FileText size={15} /> Bon de livraison
                </a>
              </DropdownMenuItem>
              {order.label_pdf_url && (
                <DropdownMenuItem asChild>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${order.label_pdf_url}`} target="_blank" rel="noreferrer" data-testid="depot-view-label" className="flex items-center gap-2 cursor-pointer">
                    <Tag size={15} /> Étiquette
                  </a>
                </DropdownMenuItem>
              )}
              {order.label_pdf_url && (
                <DropdownMenuItem onClick={printOrderLabel} data-testid="depot-print-label" className="gap-2">
                  <Printer size={15} /> Imprimer l'étiquette
                </DropdownMenuItem>
              )}
              {order.tracking_number && (
                <DropdownMenuItem asChild>
                  <a
                    href={`https://www.chronopost.fr/tracking-no-cms/suivi-page?listeNumerosLT=${order.tracking_number}`}
                    target="_blank" rel="noreferrer" data-testid="depot-track-parcel" className="flex items-center gap-2 cursor-pointer"
                  >
                    <Truck size={15} /> Suivre ce colis ({order.tracking_number})
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => { setTrackingNumberInput(order.tracking_number || ""); setTrackingDialogOpen(true); }} data-testid="depot-set-tracking-number" className="gap-2">
                <Truck size={15} /> {order.tracking_number ? "Modifier" : "Renseigner"} le numéro de suivi
              </DropdownMenuItem>
              {order.invoice_pdf_url ? (
                <DropdownMenuItem asChild>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${order.invoice_pdf_url}`} target="_blank" rel="noreferrer" data-testid="depot-view-invoice" className="flex items-center gap-2 cursor-pointer">
                    <Receipt size={15} /> Facture ({order.invoice_lines_matched || 0} prix liés)
                  </a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={pickInvoice} data-testid="depot-attach-invoice" className="gap-2">
                  <Upload size={15} /> Ajouter la facture fournisseur
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setSendOpen(true)} data-testid="depot-send-order" className="gap-2">
                <Send size={15} /> Envoyer à une boutique
              </DropdownMenuItem>
              <DropdownMenuItem onClick={removeOrder} data-testid="depot-delete-order" className="text-destructive gap-2">
                <Trash2 size={15} /> Supprimer la commande
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <input ref={invoiceInputRef} type="file" accept="application/pdf" className="hidden" onChange={attachInvoice} data-testid="depot-invoice-file-input" />
          </div>
        </div>

        <div className="h-2 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-primary transition-all duration-300" style={{ width: `${percent}%` }} />
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-2 no-print" data-testid="depot-line-hint">
        Cliquez sur une ligne pour valider une unité. Pour remettre une ligne à zéro, maintenez le clic appuyé (~1 seconde) — un simple clic ne réinitialise jamais la quantité, même une fois le maximum atteint.
      </p>
      <div className="space-y-3" data-testid="depot-line-list">
        {(displayOrder.lines || []).length === 0 && <p className="text-muted-foreground">Aucune ligne détectée automatiquement dans ce bon de livraison.</p>}
        {(displayOrder.lines || []).map((line) => {
          const done = line.quantite_picked >= line.quantite_attendue;
          const justScanned = flashLineId === line.id;
          const hasIssue = !!line.issue_type;
          const isHolding = holdingLineId === line.id;
          return (
            <div
              key={line.id}
              role="button"
              tabIndex={0}
              onClick={() => handleLineClick(line.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); tap(line.id); } }}
              onPointerDown={() => startHold(line.id)}
              onPointerUp={cancelHold}
              onPointerLeave={cancelHold}
              onPointerCancel={cancelHold}
              className={`relative overflow-hidden rounded-xl border-2 border-dashed p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 min-h-[64px] cursor-pointer select-none active:scale-[0.99] transition-colors duration-300 ${
                isHolding ? "border-amber-500 bg-amber-500/10" : justScanned ? "border-sky-500 bg-sky-500/10" : hasIssue ? "border-red-500/50 bg-red-500/10" : done ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-card hover:border-primary/60"
              }`}
              data-testid={`depot-line-${line.id}`}
            >
              {isHolding && (
                <div
                  className="absolute inset-x-0 bottom-0 h-1 bg-amber-500 origin-left"
                  style={{ animation: `depot-hold-progress ${HOLD_MS}ms linear forwards` }}
                  data-testid={`depot-line-hold-progress-${line.id}`}
                />
              )}              <div className="min-w-0">
                <p className={`font-medium break-words ${hasIssue ? "text-red-600" : ""}`}>{line.description}</p>
                <p className={`text-xs mt-1 break-words ${hasIssue ? "text-red-600/90" : "text-muted-foreground"}`}>
                  {line.ugs && <>UGS : {line.ugs} </>}
                  Étagère : {line.etagere} | Colonne : {line.colonne} | Tiroir : {line.tiroir} | Bac : {line.bac}
                </p>
                {hasIssue && (
                  <p className="text-xs font-semibold text-red-600 mt-1 flex items-center gap-1" data-testid={`depot-line-issue-${line.id}`}>
                    <AlertTriangle size={13} />
                    {ISSUE_LABEL_MAP[line.issue_type]}
                    {line.issue_type === "qte_manquante" && ` — ${line.issue_quantite_manquante} manquant(s)`}
                  </p>
                )}
                {line.prix_achat_ht != null && line.prix_achat_ht > 0 && (
                  <p className="text-xs text-emerald-600 mt-0.5" data-testid={`depot-line-prix-achat-${line.id}`}>
                    Achat fournisseur : {Number(line.prix_achat_ht).toFixed(2)} € H.T / unité
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={(e) => { e.stopPropagation(); openIssueDialog(line); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                  data-testid={`depot-line-issue-button-${line.id}`}
                  title="Signaler un problème"
                  className={`min-h-[44px] min-w-[44px] rounded-full border-2 flex items-center justify-center transition-colors duration-150 active:scale-95 ${
                    hasIssue ? "bg-red-500 border-red-500 text-white" : "bg-card border-foreground/20 text-muted-foreground hover:border-red-400 hover:text-red-500"
                  }`}
                >
                  <AlertTriangle size={18} />
                </button>
                <LinePill line={line} />
              </div>
            </div>
          );
        })}
      </div>

      {order.note_client && (
        <div
          className="mt-4 rounded-xl border-2 border-dashed border-amber-400/60 bg-amber-500/10 p-4 flex gap-3"
          data-testid="depot-order-note-client"
        >
          <StickyNote size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Notes du client</p>
            <p className="text-sm text-foreground whitespace-pre-wrap break-words">{order.note_client}</p>
          </div>
        </div>
      )}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="depot-send-dialog">
          <DialogHeader><DialogTitle>Envoyer le bon de livraison à une boutique</DialogTitle></DialogHeader>
          <Select value={targetShop} onValueChange={setTargetShop}>
            <SelectTrigger data-testid="depot-send-shop-select"><SelectValue placeholder="Choisir une boutique" /></SelectTrigger>
            <SelectContent>{shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button data-testid="depot-send-confirm-button" onClick={sendToShop} disabled={sending} className="gap-2">
              {sending && <Loader2 size={16} className="animate-spin" />} Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={trackingDialogOpen} onOpenChange={setTrackingDialogOpen}>
        <DialogContent data-testid="depot-tracking-dialog">
          <DialogHeader><DialogTitle>Numéro de suivi Chronopost</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            Format habituel : 2 lettres + 9 chiffres + 2 lettres (ex. XY123456789FR), normalement extrait
            automatiquement de l'étiquette jointe — à corriger ici si l'extraction s'est trompée ou si aucune
            étiquette n'a été jointe.
          </p>
          <Input
            value={trackingNumberInput}
            onChange={(e) => setTrackingNumberInput(e.target.value.toUpperCase())}
            placeholder="XY123456789FR"
            data-testid="depot-tracking-number-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTrackingDialogOpen(false)}>Annuler</Button>
            <Button onClick={saveTrackingNumber} disabled={savingTrackingNumber} data-testid="depot-tracking-number-save">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!issueDialogLine} onOpenChange={(o) => !o && setIssueDialogLine(null)}>
        <DialogContent data-testid="depot-issue-dialog">
          <DialogHeader><DialogTitle>Signaler un problème</DialogTitle></DialogHeader>
          {issueDialogLine && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground break-words">{issueDialogLine.description}</p>
              <Select value={issueType} onValueChange={setIssueType}>
                <SelectTrigger data-testid="depot-issue-type-select"><SelectValue placeholder="Type de problème" /></SelectTrigger>
                <SelectContent>
                  {ISSUE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {issueType === "qte_manquante" && (
                <input
                  type="number"
                  min={1}
                  max={issueDialogLine.quantite_attendue}
                  value={issueQty}
                  onChange={(e) => setIssueQty(e.target.value)}
                  placeholder={`Quantité manquante (sur ${issueDialogLine.quantite_attendue})`}
                  data-testid="depot-issue-qty-input"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              )}
            </div>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {issueDialogLine?.issue_type ? (
              <Button variant="outline" onClick={clearIssue} data-testid="depot-issue-clear-button" className="gap-2">
                <X size={15} /> Retirer le signalement
              </Button>
            ) : <span />}
            <Button onClick={submitIssue} data-testid="depot-issue-submit-button">Signaler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={soundSettingsOpen} onOpenChange={setSoundSettingsOpen}>
        <DialogContent data-testid="depot-sound-settings-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sons du scanner</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium flex items-center gap-1.5"><Volume2 size={14} /> Volume</label>
                <span className="text-xs text-muted-foreground" data-testid="depot-sound-volume-value">{Math.round(soundSettings.volume * 100)}%</span>
              </div>
              <Slider
                data-testid="depot-sound-volume-slider"
                value={[soundSettings.volume * 100]}
                min={0}
                max={100}
                step={5}
                onValueChange={([v]) => updateSoundSettings({ volume: v / 100 })}
              />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Bruit scan valide (ligne complète)</p>
              <RadioGroup
                value={soundSettings.validSoundId}
                onValueChange={(v) => updateSoundSettings({ validSoundId: v })}
                className="space-y-1"
                data-testid="depot-sound-valid-group"
              >
                {VALID_SOUNDS.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" htmlFor={`valid-${s.id}`}>
                      <RadioGroupItem value={s.id} id={`valid-${s.id}`} data-testid={`depot-sound-valid-${s.id}`} />
                      {s.label}
                    </label>
                    <button
                      type="button"
                      onClick={() => previewSound(s.id, "valid", soundSettings.volume)}
                      title="Écouter"
                      data-testid={`depot-sound-valid-preview-${s.id}`}
                      className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150"
                    >
                      <Play size={14} />
                    </button>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Bruit scan partiel (il en reste à scanner)</p>
              <p className="text-xs text-muted-foreground">
                Joué quand l'article scanné est le bon mais que la quantité demandée sur la ligne n'est pas
                encore atteinte — pour distinguer "continue de scanner" de "ligne terminée".
              </p>
              <RadioGroup
                value={soundSettings.partialSoundId}
                onValueChange={(v) => updateSoundSettings({ partialSoundId: v })}
                className="space-y-1"
                data-testid="depot-sound-partial-group"
              >
                {PARTIAL_SOUNDS.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" htmlFor={`partial-${s.id}`}>
                      <RadioGroupItem value={s.id} id={`partial-${s.id}`} data-testid={`depot-sound-partial-${s.id}`} />
                      {s.label}
                    </label>
                    <button
                      type="button"
                      onClick={() => previewSound(s.id, "partial", soundSettings.volume)}
                      title="Écouter"
                      data-testid={`depot-sound-partial-preview-${s.id}`}
                      className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150"
                    >
                      <Play size={14} />
                    </button>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Bruit scan invalide</p>
              <RadioGroup
                value={soundSettings.invalidSoundId}
                onValueChange={(v) => updateSoundSettings({ invalidSoundId: v })}
                className="space-y-1"
                data-testid="depot-sound-invalid-group"
              >
                {INVALID_SOUNDS.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer" htmlFor={`invalid-${s.id}`}>
                      <RadioGroupItem value={s.id} id={`invalid-${s.id}`} data-testid={`depot-sound-invalid-${s.id}`} />
                      {s.label}
                    </label>
                    <button
                      type="button"
                      onClick={() => previewSound(s.id, "invalid", soundSettings.volume)}
                      title="Écouter"
                      data-testid={`depot-sound-invalid-preview-${s.id}`}
                      className="p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors duration-150"
                    >
                      <Play size={14} />
                    </button>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setSoundSettingsOpen(false)} data-testid="depot-sound-settings-close">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
